import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";
import {
  FaWhatsapp,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaGem,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const fallbackItems = [
  {
    _id: "1",
    itemCode: "JW-1001",
    categoryName: "Necklace",
    productName: "Bridal Necklace",
    designName: "Royal Heritage Necklace",
    purityName: "22KT Gold",
    netWt: "42.350",
    totalWt: "44.120",
    grossWt: "44.120",
    images: [
      {
        url: "https://images.unsplash.com/photo-1601121141461-9d6647bca1ed?auto=format&fit=crop&w=1400&q=90",
      },
    ],
  },
  {
    _id: "2",
    itemCode: "JW-1002",
    categoryName: "Ring",
    productName: "Diamond Ring",
    designName: "Celestial Diamond Ring",
    purityName: "18KT Gold",
    netWt: "6.250",
    totalWt: "6.420",
    grossWt: "6.420",
    images: [
      {
        url: "https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=1400&q=90",
      },
    ],
  },
  {
    _id: "3",
    itemCode: "JW-1003",
    categoryName: "Bangle",
    productName: "Gold Bangle",
    designName: "Classic Temple Bangle",
    purityName: "22KT Gold",
    netWt: "28.800",
    totalWt: "29.200",
    grossWt: "29.200",
    images: [
      {
        url: "https://images.unsplash.com/photo-1611652022419-a9419f74343d?auto=format&fit=crop&w=1400&q=90",
      },
    ],
  },
  {
    _id: "4",
    itemCode: "JW-1004",
    categoryName: "Earrings",
    productName: "Gold Earrings",
    designName: "Floral Stud Earrings",
    purityName: "22KT Gold",
    netWt: "8.450",
    totalWt: "8.650",
    grossWt: "8.650",
    images: [
      {
        url: "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&w=1400&q=90",
      },
    ],
  },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1] },
  },
};

const imageReveal = {
  hidden: { opacity: 0, scale: 1.08, filter: "blur(10px)" },
  show: {
    opacity: 1,
    scale: 1,
    filter: "blur(0px)",
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] },
  },
};

const getImageUrl = (item) =>
  item?.images?.[0]?.url ||
  "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=1400&q=90";

const formatWt = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `${value} g`;
};

const LuxuryBrochurePage = ({ brochureData }) => {
  const { slug } = useParams();
  const [activeItem, setActiveItem] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const brochure = brochureData || {
    title: "The Signature Collection",
    subtitle: "A curated jewellery lookbook crafted for timeless celebrations.",
    brandName: "Sparkle Jewellery",
    tagline: "Luxury in every detail",
    phone: "+91 98765 43210",
    whatsapp: "919876543210",
    location: "Pune, Maharashtra",
    slug,
    items: fallbackItems,
  };

  const items = useMemo(() => brochure?.items || [], [brochure]);
  const heroItem = items?.[0];
  const featuredItems = items.slice(1, 4);

  const openLightbox = (item, index) => {
    setActiveItem(item);
    setActiveIndex(index);
  };

  const closeLightbox = () => setActiveItem(null);

  const goNext = () => {
    const nextIndex = (activeIndex + 1) % items.length;
    setActiveIndex(nextIndex);
    setActiveItem(items[nextIndex]);
  };

  const goPrev = () => {
    const prevIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
    setActiveIndex(prevIndex);
    setActiveItem(items[prevIndex]);
  };

  const whatsappText = encodeURIComponent(
    `Hi, I am interested in the jewellery brochure: ${brochure.title}`,
  );
  const whatsappUrl = `https://wa.me/${brochure.whatsapp}?text=${whatsappText}`;

  return (
    <div className="min-h-screen overflow-hidden bg-[#090706] text-white">
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#b88746]/20 blur-[120px]" />
        <div className="absolute top-[30%] -right-40 h-[560px] w-[560px] rounded-full bg-[#f3d48b]/10 blur-[140px]" />
        <div className="absolute bottom-0 left-1/3 h-[380px] w-[380px] rounded-full bg-white/5 blur-[110px]" />
      </div>

      <header className="fixed left-0 right-0 top-0 z-40 border-b border-white/10 bg-[#090706]/70 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.45em] text-[#d6b56d]">
              {brochure.tagline}
            </p>
            <h1 className="font-serif text-lg tracking-wide text-white md:text-2xl">
              {brochure.brandName}
            </h1>
          </div>

          <div className="hidden items-center gap-6 text-xs text-white/70 md:flex">
            <span className="inline-flex items-center gap-2">
              <FaPhoneAlt className="text-[#d6b56d]" /> {brochure.phone}
            </span>
            <span className="inline-flex items-center gap-2">
              <FaMapMarkerAlt className="text-[#d6b56d]" /> {brochure.location}
            </span>
          </div>

          <a
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-[#d6b56d]/50 bg-[#d6b56d] px-4 py-2 text-xs font-semibold text-[#15100b] shadow-[0_0_35px_rgba(214,181,109,0.25)] transition hover:-translate-y-0.5 hover:bg-[#f1d184]"
          >
            <FaWhatsapp className="text-base" /> Enquire
          </a>
        </div>
      </header>

      <main className="relative z-10">
        <section className="relative flex min-h-screen items-center pt-24">
          <div className="absolute inset-0">
            <img
              src={getImageUrl(heroItem)}
              alt={heroItem?.designName || "Jewellery"}
              className="h-full w-full object-cover opacity-55"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#090706] via-[#090706]/82 to-[#090706]/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#090706] via-transparent to-[#090706]/60" />
          </div>

          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 py-20 lg:grid-cols-[0.9fr_1.1fr] lg:px-8"
          >
            <div>
              <motion.div
                variants={fadeUp}
                className="mb-6 inline-flex items-center gap-3 rounded-full border border-[#d6b56d]/30 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.28em] text-[#d6b56d] backdrop-blur-xl"
              >
                <FaGem /> Premium Lookbook
              </motion.div>

              <motion.h2
                variants={fadeUp}
                className="max-w-3xl font-serif text-5xl leading-[0.95] tracking-[-0.04em] text-white md:text-7xl xl:text-8xl"
              >
                {brochure.title}
              </motion.h2>

              <motion.p
                variants={fadeUp}
                className="mt-7 max-w-xl text-base leading-8 text-white/68 md:text-lg"
              >
                {brochure.subtitle}
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <a
                  href="#collection"
                  className="rounded-full bg-white px-7 py-3 text-sm font-semibold text-[#100c08] transition hover:-translate-y-1 hover:bg-[#f4dfad]"
                >
                  Explore Collection
                </a>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full border border-white/25 px-7 py-3 text-sm font-semibold text-white transition hover:-translate-y-1 hover:border-[#d6b56d] hover:text-[#d6b56d]"
                >
                  Book Private Viewing
                </a>
              </motion.div>
            </div>

            <motion.div
              variants={imageReveal}
              className="relative hidden lg:block"
            >
              <div className="absolute -inset-6 rounded-[3rem] bg-gradient-to-br from-[#d6b56d]/30 via-white/5 to-transparent blur-2xl" />
              <div className="relative overflow-hidden rounded-[2.5rem] border border-white/15 bg-white/10 p-3 shadow-[0_40px_120px_rgba(0,0,0,0.65)] backdrop-blur-xl">
                <img
                  src={getImageUrl(heroItem)}
                  alt={heroItem?.designName || "Featured jewellery"}
                  className="h-[620px] w-full rounded-[2rem] object-cover"
                />
                <div className="absolute bottom-8 left-8 right-8 rounded-3xl border border-white/15 bg-black/35 p-5 backdrop-blur-2xl">
                  <p className="text-[10px] uppercase tracking-[0.35em] text-[#d6b56d]">
                    Featured Design
                  </p>
                  <h3 className="mt-2 font-serif text-3xl text-white">
                    {heroItem?.designName || heroItem?.productName}
                  </h3>
                  <p className="mt-2 text-sm text-white/65">
                    {heroItem?.purityName} • Net Wt {formatWt(heroItem?.netWt)}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        <section className="border-y border-white/10 bg-[#110d0a]/90 py-7">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-5 px-5 text-center md:grid-cols-4 lg:px-8">
            {[
              [items.length, "Curated Designs"],
              ["22KT / 18KT", "Premium Purity"],
              ["Handpicked", "For You"],
              ["Private", "Viewing Available"],
            ].map(([value, label]) => (
              <div key={label} className="border-r border-white/10 last:border-r-0">
                <p className="font-serif text-3xl text-[#d6b56d] md:text-4xl">
                  {value}
                </p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.28em] text-white/45">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section id="collection" className="relative py-24 md:py-32">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.75 }}
              className="mx-auto max-w-3xl text-center"
            >
              <p className="text-xs uppercase tracking-[0.4em] text-[#d6b56d]">
                The Collection
              </p>
              <h2 className="mt-4 font-serif text-4xl leading-tight text-white md:text-6xl">
                Crafted to feel rare, personal and unforgettable.
              </h2>
              <p className="mt-5 text-sm leading-7 text-white/55 md:text-base">
                Every piece is presented with a premium visual experience, making
                it easier for customers to explore, shortlist and enquire.
              </p>
            </motion.div>

            <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-3">
              {featuredItems.map((item, index) => (
                <motion.button
                  key={item._id || item.itemCode}
                  type="button"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ duration: 0.7, delay: index * 0.12 }}
                  onClick={() => openLightbox(item, index + 1)}
                  className={`group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] text-left shadow-[0_30px_80px_rgba(0,0,0,0.35)] ${
                    index === 1 ? "md:-mt-10" : ""
                  }`}
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={getImageUrl(item)}
                      alt={item.designName || item.productName}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-85" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-[10px] uppercase tracking-[0.32em] text-[#d6b56d]">
                      {item.categoryName || "Jewellery"}
                    </p>
                    <h3 className="mt-2 font-serif text-3xl leading-tight text-white">
                      {item.designName || item.productName}
                    </h3>
                    <p className="mt-3 text-sm text-white/60">
                      {item.purityName} • {formatWt(item.netWt)}
                    </p>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </section>

        {heroItem && (
          <section className="relative overflow-hidden bg-[#f6efe1] py-24 text-[#17110b] md:py-32">
            <div className="absolute inset-0 opacity-[0.08] [background-image:radial-gradient(#8a6428_1px,transparent_1px)] [background-size:22px_22px]" />
            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 lg:grid-cols-2 lg:px-8">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.85 }}
                className="relative"
              >
                <div className="absolute -inset-5 rounded-[3rem] border border-[#bd9550]/25" />
                <img
                  src={getImageUrl(heroItem)}
                  alt={heroItem.designName || heroItem.productName}
                  className="relative h-[640px] w-full rounded-[2.5rem] object-cover shadow-[0_35px_100px_rgba(47,32,12,0.35)]"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.85 }}
              >
                <p className="text-xs uppercase tracking-[0.42em] text-[#a37631]">
                  Signature Piece
                </p>
                <h2 className="mt-5 font-serif text-5xl leading-tight tracking-[-0.03em] md:text-7xl">
                  {heroItem.designName || heroItem.productName}
                </h2>
                <p className="mt-6 max-w-xl text-base leading-8 text-[#4d4034]">
                  A statement design selected for elegance, craftsmanship and
                  premium customer presentation. Perfect for creating an
                  exclusive buying experience.
                </p>

                <div className="mt-10 grid max-w-xl grid-cols-2 gap-4">
                  {[
                    ["Item Code", heroItem.itemCode],
                    ["Purity", heroItem.purityName],
                    ["Net Weight", formatWt(heroItem.netWt)],
                    ["Gross Weight", formatWt(heroItem.grossWt)],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="rounded-3xl border border-[#c9a45d]/25 bg-white/60 p-5 shadow-sm backdrop-blur-xl"
                    >
                      <p className="text-[10px] uppercase tracking-[0.24em] text-[#9b7232]">
                        {label}
                      </p>
                      <p className="mt-2 text-lg font-semibold text-[#17110b]">
                        {value || "-"}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>
        )}

        <section className="py-24 md:py-32">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-[#d6b56d]">
                  Complete Lookbook
                </p>
                <h2 className="mt-4 font-serif text-4xl text-white md:text-6xl">
                  Explore every selected design
                </h2>
              </div>
              <p className="max-w-md text-sm leading-7 text-white/50">
                Tap any design to view it in a premium focus mode with product
                details and enquiry action.
              </p>
            </div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
            >
              {items.map((item, index) => (
                <motion.button
                  key={item._id || item.itemCode || index}
                  variants={fadeUp}
                  type="button"
                  onClick={() => openLightbox(item, index)}
                  className="group overflow-hidden rounded-[1.8rem] border border-white/10 bg-white/[0.035] text-left transition hover:-translate-y-2 hover:border-[#d6b56d]/50 hover:bg-white/[0.06]"
                >
                  <div className="relative aspect-[4/4.8] overflow-hidden bg-[#17100b]">
                    <img
                      src={getImageUrl(item)}
                      alt={item.designName || item.productName}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
                    <span className="absolute left-4 top-4 rounded-full border border-white/20 bg-black/35 px-3 py-1 text-[10px] font-semibold text-white/85 backdrop-blur-xl">
                      {item.itemCode || "Design"}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif text-2xl leading-tight text-white">
                      {item.designName || item.productName}
                    </h3>
                    <p className="mt-2 text-xs leading-6 text-white/48">
                      {item.categoryName || "-"} • {item.purityName || "-"}
                    </p>
                    <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4 text-xs text-white/55">
                      <span>Net Wt</span>
                      <span className="font-semibold text-[#d6b56d]">
                        {formatWt(item.netWt)}
                      </span>
                    </div>
                  </div>
                </motion.button>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="relative overflow-hidden border-t border-white/10 bg-[#120d09] py-24 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(214,181,109,0.16),transparent_55%)]" />
          <div className="relative mx-auto max-w-3xl px-5">
            <p className="text-xs uppercase tracking-[0.4em] text-[#d6b56d]">
              Private Assistance
            </p>
            <h2 className="mt-5 font-serif text-4xl leading-tight text-white md:text-6xl">
              Found something beautiful?
            </h2>
            <p className="mt-5 text-base leading-8 text-white/58">
              Connect with our team to check availability, pricing, customisation
              and showroom appointment details.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-3 rounded-full bg-[#d6b56d] px-8 py-4 text-sm font-bold text-[#120d09] transition hover:-translate-y-1 hover:bg-[#f1d184]"
              >
                <FaWhatsapp className="text-lg" /> Enquire on WhatsApp
              </a>
              <a
                href={`tel:${brochure.phone}`}
                className="inline-flex items-center gap-3 rounded-full border border-white/20 px-8 py-4 text-sm font-bold text-white transition hover:-translate-y-1 hover:border-[#d6b56d] hover:text-[#d6b56d]"
              >
                <FaPhoneAlt /> Call Now
              </a>
            </div>
          </div>
        </section>
      </main>

      <AnimatePresence>
        {activeItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-xl"
          >
            <button
              type="button"
              onClick={closeLightbox}
              className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
            >
              <FaTimes />
            </button>

            {items.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-5 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20 md:flex"
                >
                  <FaChevronLeft />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-5 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20 md:flex"
                >
                  <FaChevronRight />
                </button>
              </>
            )}

            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ duration: 0.45 }}
              className="grid max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-[2rem] border border-white/10 bg-[#120d09] shadow-[0_40px_140px_rgba(0,0,0,0.8)] md:grid-cols-[1.1fr_0.9fr]"
            >
              <div className="max-h-[90vh] bg-black">
                <img
                  src={getImageUrl(activeItem)}
                  alt={activeItem.designName || activeItem.productName}
                  className="h-full min-h-[420px] w-full object-cover"
                />
              </div>

              <div className="overflow-y-auto p-7 md:p-10">
                <p className="text-xs uppercase tracking-[0.4em] text-[#d6b56d]">
                  {activeItem.categoryName || "Jewellery"}
                </p>
                <h3 className="mt-4 font-serif text-4xl leading-tight text-white md:text-5xl">
                  {activeItem.designName || activeItem.productName}
                </h3>
                <p className="mt-4 text-sm leading-7 text-white/55">
                  A premium selected design from this curated brochure. Contact
                  our team for price, availability and customization options.
                </p>

                <div className="mt-8 grid grid-cols-2 gap-3">
                  {[
                    ["Item Code", activeItem.itemCode],
                    ["Purity", activeItem.purityName],
                    ["Net Weight", formatWt(activeItem.netWt)],
                    ["Total Weight", formatWt(activeItem.totalWt)],
                    ["Gross Weight", formatWt(activeItem.grossWt)],
                    ["Product", activeItem.productName],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                    >
                      <p className="text-[10px] uppercase tracking-[0.24em] text-white/35">
                        {label}
                      </p>
                      <p className="mt-2 text-sm font-semibold text-white">
                        {value || "-"}
                      </p>
                    </div>
                  ))}
                </div>

                <a
                  href={`https://wa.me/${brochure.whatsapp}?text=${encodeURIComponent(
                    `Hi, I am interested in ${activeItem.designName || activeItem.productName} (${activeItem.itemCode || ""})`,
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#d6b56d] px-7 py-4 text-sm font-bold text-[#120d09] transition hover:bg-[#f1d184]"
                >
                  <FaWhatsapp className="text-lg" /> Enquire About This Design
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LuxuryBrochurePage;
